import React, { useState } from 'react';
import {
  KeywordPhrase,
  KeywordWeight
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { ExitIconSmall } from 'src/components/SvgIcons/SvgIcons';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { hexToRgba } from '@stackline/ui';
import { linearProgressClasses, LinearProgress, Popover, Box } from '@mui/material';
import { safeDivide } from 'src/utils/app';
import { useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import styled from '@emotion/styled';
import { KeywordFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import ScrollableContainer from 'src/components/BeaconV2Components/ScrollableContainer';

const TotalWeightBadge = ({ children, color }: { children: React.ReactNode; color: string }) => {
  return (
    <Flex
      sx={{
        minHeight: '24px',
        padding: '2px 8px',
        background: color,
        borderRadius: '24px'
      }}
      justifyContent="center"
      alignItems="center"
    >
      <Text color="white" fontWeight="700" variant="body3">
        {children}
      </Text>
    </Flex>
  );
};

const KeywordPhraseContainer = ({
  children,
  paddingRight,
  cursor,
  color
}: {
  children: React.ReactNode;
  paddingRight: string;
  cursor: string;
  color: string;
}) => {
  return (
    <Flex
      sx={{
        minHeight: '32px',
        padding: '4px',
        paddingRight,
        background: color,
        borderRadius: '24px',
        justifyContent: 'center',
        alignItems: 'center',
        cursor,
        gap: '8px'
      }}
    >
      {children}
    </Flex>
  );
};

const NumWeightsBadge = ({ count, color }: { count: number; color: string }) => {
  return (
    <>
      <Flex
        sx={{ background: color, borderRadius: '9999px', width: '24px', height: '24px', padding: '6px' }}
        justifyContent="center"
        alignItems="center"
      >
        <Text fontWeight="500" variant="body3" color="white">
          {count}
        </Text>
      </Flex>
    </>
  );
};

export interface KeywordColor {
  primary: string;
  secondary: string;
  tooltipPrimary: string;
}

/**
 * Keyword phrase component that displays the total weight of a keyword phrase and a popup that displays the weights breakdown of a keyword phrase (if applicable)
 */
export const PhraseWithMetadata = ({
  phrase,
  metadata,
  hideKeywords,
  id,
  activePhraseId,
  setActivePhraseId,
  keywordFilter
}: KeywordPhrase & {
  hideKeywords?: boolean;
  id: number;
  activePhraseId: number | null;
  setActivePhraseId: (id: number) => void;
  keywordFilter: KeywordFilter;
}) => {
  const metricFormatter = useMetricFormatter();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const showPopup = activePhraseId === id;

  if (!metadata || hideKeywords) {
    return <Text variant="body1">{phrase}</Text>;
  }

  const { totalWeight, weights } = metadata;
  const shouldShowWeightsBreakdown = weights && weights.length > 1;

  const hasProductiveKeyword = weights ? weights.some((weight) => weight.sku_level_weight > 0) : false;

  // TODO: color for Red Pill is not designed in Figma yet
  const color: KeywordColor = hasProductiveKeyword
    ? { primary: '#185450', secondary: '#E3F3E6', tooltipPrimary: '#71C184' }
    : { primary: '#FF534A', secondary: '#FFEEED', tooltipPrimary: '#71C184' };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setActivePhraseId(id);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActivePhraseId(null);
  };

  return (
    <Flex sx={{ margin: '0 2px' }}>
      <Flex onClick={handleClick}>
        <KeywordPhraseContainer
          paddingRight={shouldShowWeightsBreakdown ? '4px' : '8px'}
          cursor={shouldShowWeightsBreakdown ? 'pointer' : 'default'}
          color={color.secondary}
        >
          <TotalWeightBadge color={color.primary}>
            {metricFormatter(totalWeight, METRICTYPE.VOLUME, { showFullValue: false })}
          </TotalWeightBadge>
          <Text fontWeight="500" variant="body2" sx={{ color: color.primary }}>
            {phrase}
          </Text>
          {shouldShowWeightsBreakdown && <NumWeightsBadge count={weights.length} color={color.primary} />}
        </KeywordPhraseContainer>
      </Flex>

      {shouldShowWeightsBreakdown && (
        <Popover
          open={showPopup}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          marginThreshold={48}
          slotProps={{
            paper: {
              sx: {
                marginTop: '8px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
              }
            }
          }}
        >
          <EmbeddedKeywordsDetails
            onClick={handleClose}
            title={keywordFilter}
            weights={weights}
            totalWeight={totalWeight}
            color={color}
          />
        </Popover>
      )}
    </Flex>
  );
};

/**
 * Embedded keywords popup component that displays the weights breakdown of a keyword phrase
 */
const EmbeddedKeywordsDetails = ({
  onClick,
  weights,
  totalWeight,
  color,
  title
}: {
  onClick: () => void;
  weights: KeywordWeight[];
  totalWeight: number;
  color: KeywordColor;
  title: string;
}) => {
  const metricFormatter = useMetricFormatter();
  const maxWeight = weights.reduce((max, weight) => Math.max(max, weight.subcat_level_weight), 0);
  const maxWeightLength = metricFormatter(maxWeight, METRICTYPE.VOLUME, { showFullValue: false }).length;

  const { marginLeft } = (() => {
    switch (maxWeightLength) {
      case 1:
        return { marginLeft: '-33px' };
      case 2:
        return { marginLeft: '-26px' };
      case 3:
        return { marginLeft: '-19px' };
      case 4:
        return { marginLeft: '-15px' };
      case 5:
        return { marginLeft: '-9px' };
      default:
        return { marginLeft: '0px' };
    }
  })();

  return (
    <Flex
      sx={{
        width: '500px',
        maxHeight: '394px',
        background: 'white',
        borderRadius: '8px',
        border: '1px #E8E8ED solid',
        paddingBottom: '16px'
      }}
      flexDirection="column"
    >
      <EmbeddedKeywordsHeader onClick={onClick} />
      <WeightsBreakdownDetails
        weights={weights}
        totalWeight={totalWeight}
        color={color}
        marginLeft={marginLeft}
        title={title}
      />
    </Flex>
  );
};

const EmbeddedKeywordsHeader = ({ onClick }: { onClick: () => void }) => {
  return (
    <Flex
      sx={{
        minHeight: '48px',
        borderBottom: '1px #E8E8ED solid',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative'
      }}
    >
      <Text
        variant="body2"
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '16px',
          fontWeight: '700'
        }}
      >
        Embedded Keywords
      </Text>
      {/* TODO: Design QA: this icon and make sure its the correct one */}
      <ExitIconSmall
        style={{ width: '12px', height: '12px', position: 'absolute', right: '16px', cursor: 'pointer' }}
        onClick={onClick}
      />
    </Flex>
  );
};

const WeightsBreakdownDetails = ({
  weights,
  totalWeight,
  color,
  marginLeft,
  title
}: {
  weights: KeywordWeight[];
  totalWeight: number;
  color: KeywordColor;
  marginLeft: string;
  title: string;
}) => {
  return (
    <ScrollableContainer scrollbarWidth="4px" thumbColor="#BEC7D3">
      <Flex
        width="100%"
        padding="24px"
        paddingBottom={0}
        flexDirection="column"
        justifyContent="start"
        alignItems="center"
      >
        <Text variant="body3" sx={{ marginBottom: '24px', lineHeight: '18px' }}>
          {`The keywords below are embedded in the selected phrase along with the ${
            title === KeywordFilter.Popularity ? 'search volume' : 'associated organic traffic opportunity'
          } and the amount earned by this product.`}
        </Text>

        {/* Legend for traffic bars */}
        <Flex justifyContent="start" sx={{ width: '100%', marginBottom: '24px', gap: '32px' }}>
          <Flex alignItems="center" sx={{ gap: '8px', height: '18px' }}>
            <Box sx={{ width: '20px', height: '4px', backgroundColor: color.primary, borderRadius: '2px' }} />
            <Text variant="body3" fontWeight="500">
              Organic Traffic Earned
            </Text>
          </Flex>
          <Flex alignItems="center" sx={{ gap: '8px', height: '18px' }}>
            <Box sx={{ width: '20px', height: '4px', backgroundColor: color.tooltipPrimary, borderRadius: '2px' }} />
            <Text variant="body3" fontWeight="500">
              Organic Traffic Opportunity
            </Text>
          </Flex>
        </Flex>

        <Flex flexDirection="column" alignItems="stretch" sx={{ width: '100%' }}>
          {weights
            .sort((a, b) => b.subcat_level_weight - a.subcat_level_weight)
            .map((weight, index) => (
              <Box key={`${index}-${weight.keyword}`} sx={{ marginBottom: '16px' }}>
                <KeywordWeightDetails
                  {...weight}
                  totalWeight={totalWeight}
                  color={color.tooltipPrimary}
                  marginLeft={marginLeft}
                />
              </Box>
            ))}
        </Flex>
      </Flex>
    </ScrollableContainer>
  );
};

// TODO(tyan): Design QA: Verify the background color of progress bar (opacity)
export const ColoredProgressBar = ({ color, value }: { color: string; value: number }) => {
  const ProgressBar = styled(LinearProgress)({
    width: '224px',
    height: '8px',
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: hexToRgba('#cfd7df', 0.4)
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: color
    }
  });

  return <ProgressBar variant="determinate" value={value} />;
};

const KeywordWeightDetails = ({
  keyword,
  subcat_level_weight,
  sku_level_weight,
  totalWeight,
  color,
  marginLeft
}: KeywordWeight & {
  totalWeight: number;
  color: string;
  marginLeft: string;
}) => {
  const metricFormatter = useMetricFormatter();

  const subcatProgressValue = Math.min(100, safeDivide(subcat_level_weight, totalWeight) * 100);
  const skuProgressValue = Math.min(100, safeDivide(sku_level_weight, totalWeight) * 100);
  return (
    <Flex
      justifyContent="start"
      alignItems="center"
      sx={{
        gap: '16px',
        marginLeft
      }}
    >
      <Flex flexDirection="column" alignItems="stretch" sx={{ gap: '6px' }}>
        <Flex flexDirection="row" justifyContent="start" alignItems="center" sx={{ gap: '16px' }}>
          <Text sx={{ width: '40px', textAlign: 'right' }} variant="body3">
            {metricFormatter(subcat_level_weight, METRICTYPE.VOLUME, { showFullValue: false })}
          </Text>
          <ColoredProgressBar color={color} value={subcatProgressValue} />
        </Flex>
        <Flex flexDirection="row" justifyContent="start" alignItems="center" sx={{ gap: '16px' }}>
          <Text sx={{ width: '40px', textAlign: 'right' }} variant="body3">
            {metricFormatter(sku_level_weight, METRICTYPE.VOLUME, { showFullValue: false })}
          </Text>
          <ColoredProgressBar color="#185450" value={skuProgressValue} />
        </Flex>
      </Flex>
      <Text sx={{ maxWidth: '130px' }} fontWeight="500" variant="body3">
        {keyword}
      </Text>
    </Flex>
  );
};
